import { useLayoutEffect, useRef, useState } from 'react';
import { Avatar } from '../common';
import type { DayDog } from './today';

interface Props {
  items: DayDog[];
  // Optional — when given, each chip opens its booking (Daily). When omitted,
  // chips render read-only (the Messages day strip reuses the card at a glance).
  onOpen?: (bookingId: string) => void;
}

// The In-residence dog list — fills the card (whose height is dictated by the
// On-duty card) with as many dog chips as fit across the available rows, then a
// "+N more" chip for the rest. A hidden ruler lays every chip out at the real
// width to measure how many fit; re-measures on resize.
export function ResidentsFill({ items, onOpen }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(items.length);

  useLayoutEffect(() => {
    const measure = () => {
      const wrap = wrapRef.current;
      const ruler = rulerRef.current;
      if (!wrap || !ruler) return;
      const h = wrap.clientHeight;
      const chips = Array.from(ruler.querySelectorAll<HTMLElement>('.today-name-chip'));
      // Chips fully within the available height.
      const fit = chips.filter((el) => el.offsetTop + el.offsetHeight <= h + 1).length;
      // All fit → show all; otherwise drop one to make room for the "+N more".
      setCount(fit >= items.length ? items.length : Math.max(1, fit - 1));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [items.length]);

  const shown = items.slice(0, count);
  const overflow = items.length - shown.length;

  return (
    <div className="irq-fillwrap" ref={wrapRef}>
      {/* Hidden ruler — every chip laid out at the real width for measurement. */}
      <div className="irq-ruler" ref={rulerRef} aria-hidden="true">
        {items.map((r) => (
          <span key={r.dog.id + '-' + r.bookingId} className="today-name-chip">
            <Avatar name={r.dog.name} id={r.dog.id} size={22} kind="dog" />
            <span className="name">{r.dog.name}</span>
          </span>
        ))}
      </div>

      <div className="irq-fill">
        {shown.map((r) =>
          onOpen ? (
            <button
              key={r.dog.id + '-' + r.bookingId}
              type="button"
              className="today-name-chip clickable"
              onClick={() => onOpen(r.bookingId)}
            >
              <Avatar name={r.dog.name} id={r.dog.id} size={22} kind="dog" />
              <span className="name">{r.dog.name}</span>
            </button>
          ) : (
            <span key={r.dog.id + '-' + r.bookingId} className="today-name-chip">
              <Avatar name={r.dog.name} id={r.dog.id} size={22} kind="dog" />
              <span className="name">{r.dog.name}</span>
            </span>
          ),
        )}
        {overflow > 0 && <span className="today-name-ellipsis">+{overflow} more</span>}
      </div>
    </div>
  );
}
